/**
 * Where to send a user after they sign in, from the login page's `?redirect=`.
 *
 * The parameter is attacker-controllable (anyone can send a login link), so
 * it is an open-redirect surface: only a same-origin page inside the admin
 * app is ever followed, and anything else falls back to `/admin`.
 */

const ADMIN_PREFIX = '/admin/';
const LOGIN_PATH = '/admin/login';

/**
 * The redirect target as a same-origin path (path + search + hash), or null
 * when it is not one to follow. Accepted only when it starts with `/admin/`,
 * holds no control characters, and still resolves inside `/admin/` on this
 * origin once `..` segments are applied, with a path that holds no `//` or `\`
 * (raw or percent-encoded) and is not the login page itself. Slashes in the
 * search or the hash are only text (`?q=https://…`), so they are allowed.
 */
export function safeRedirect(target: unknown): string | null {
  if (typeof target !== 'string' || !target.startsWith(ADMIN_PREFIX)) return null;
  // eslint-disable-next-line no-control-regex -- control characters are what this refuses
  if (/[\u0000-\u001f\u007f]/.test(target)) return null;
  let url: URL;
  try {
    url = new URL(target, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin || !url.pathname.startsWith(ADMIN_PREFIX)) return null;
  // The raw path, before the URL parser folded `\` into `/`: the part before any `?` or `#`.
  const rawPath = target.split(/[?#]/, 1)[0]!;
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(url.pathname);
  } catch {
    return null;
  }
  for (const form of [rawPath, url.pathname, decodedPath]) {
    if (form.includes('//') || form.includes('\\')) return null;
  }
  // Signing in must not land back on the sign-in page.
  if (url.pathname === LOGIN_PATH || url.pathname.startsWith(`${LOGIN_PATH}/`)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * The page the browser is on, as a `redirect` value for the login page, or
 * undefined when it is not one `safeRedirect` would follow (the login page
 * itself, the bare `/admin`, anything outside the admin app).
 */
export function redirectToCurrentPage(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const { pathname, search = '', hash = '' } = window.location;
  if (pathname.startsWith('/admin/login')) return undefined;
  return safeRedirect(`${pathname}${search}${hash}`) ?? undefined;
}

/** The login page's address for a full-page load, carrying `redirect` when there is one. */
export function loginHref(redirect: string | undefined): string {
  return redirect ? `/admin/login?redirect=${encodeURIComponent(redirect)}` : '/admin/login';
}
